import { z } from "zod";

// GPS stamp recorded by the server from client-reported coordinates.
export const GeoStampSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  accuracyM: z.number().nonnegative().nullable().default(null),
  at: z.number(),
});
export type GeoStamp = z.infer<typeof GeoStampSchema>;

export const TimeEntrySchema = z.object({
  uid: z.string(),
  displayName: z.string().default(""),
  locationId: z.string().nullable().default(null),
  jobId: z.string().nullable().default(null),
  shiftId: z.string().nullable().default(null),
  clockInAt: z.number(),
  clockInGeo: GeoStampSchema.nullable().default(null),
  clockOutAt: z.number().nullable().default(null),
  clockOutGeo: GeoStampSchema.nullable().default(null),
  breakMinutes: z.number().int().nonnegative().default(0),
  // approval flow: open -> submitted -> approved | rejected (manager)
  status: z.enum(["open", "submitted", "approved", "rejected"]).default("open"),
  approvedByUid: z.string().nullable().default(null),
  approvedAt: z.number().nullable().default(null),
  note: z.string().default(""),
  payPeriodId: z.string().nullable().default(null),
  createdAt: z.number(),
});
export type TimeEntry = z.infer<typeof TimeEntrySchema>;

export const ShiftStatusSchema = z.enum([
  "draft", // manager is composing
  "posted", // visible to eligible workers
  "claim_pending", // a worker claimed, awaiting manager approval
  "assigned", // approved / directly assigned
  "completed",
  "canceled",
]);
export type ShiftStatus = z.infer<typeof ShiftStatusSchema>;

export const ShiftSchema = z.object({
  title: z.string().min(1),
  locationId: z.string().nullable().default(null),
  startAt: z.number(),
  endAt: z.number(),
  status: ShiftStatusSchema.default("draft"),
  postedByUid: z.string(),
  assignedUid: z.string().nullable().default(null),
  claim: z
    .object({
      uid: z.string(),
      displayName: z.string().default(""),
      claimedAt: z.number(),
    })
    .nullable()
    .default(null),
  requiresApproval: z.boolean().default(true),
  note: z.string().default(""),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Shift = z.infer<typeof ShiftSchema>;

export const OpenJobStatusSchema = z.enum(["posted", "claimed", "completed", "canceled"]);
export type OpenJobStatus = z.infer<typeof OpenJobStatusSchema>;

/** tenants/{t}/pieceReviews/{workItemId}: a tech-logged part waiting for a manager's OK (the company asks for it). */
export const PieceReviewSchema = z.object({
  jobId: z.string(),
  jobTitle: z.string().default(""),
  workItemId: z.string(),
  techUid: z.string(),
  techName: z.string().default(""),
  description: z.string(),
  quantity: z.number(),
  rateCents: z.number().int().nonnegative(),
  amountCents: z.number().int().nonnegative(),
  status: z.enum(["pending", "approved", "declined"]).default("pending"),
  at: z.number(),
  reviewedAt: z.number().nullable().default(null),
  reviewedByName: z.string().default(""),
});
export type PieceReview = z.infer<typeof PieceReviewSchema>;

// A posted piece of work that eligible workers can pick up "near me".
export const OpenJobSchema = z.object({
  title: z.string().min(1),
  description: z.string().default(""),
  jobId: z.string().nullable().default(null), // optional link to the real job record
  lat: z.number(),
  lng: z.number(),
  geohash: z.string(), // geofire-common hash for radius queries
  address: z.string().default(""),
  windowStartAt: z.number().nullable().default(null),
  windowEndAt: z.number().nullable().default(null),
  payCents: z.number().int().nonnegative().nullable().default(null),
  status: OpenJobStatusSchema.default("posted"),
  postedByUid: z.string(),
  claimedByUid: z.string().nullable().default(null),
  claimedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type OpenJob = z.infer<typeof OpenJobSchema>;

// Off-shift availability: a tech not on the schedule raises their hand with a
// one-time GPS fix (never continuous tracking) and their drive radius.
// Auto-expires; dispatch sees who's in range of a job, and newly posted open
// jobs ping available techs within THEIR radius.
export const AvailabilitySchema = z.object({
  available: z.boolean(),
  displayName: z.string().default(""),
  lat: z.number().nullable().default(null),
  lng: z.number().nullable().default(null),
  geohash: z.string().nullable().default(null),
  radiusKm: z.number().positive().default(64), // internal km; UIs show miles
  setAt: z.number(),
  expiresAt: z.number().nullable().default(null),
});
export type Availability = z.infer<typeof AvailabilitySchema>;

export const PayPeriodSchema = z.object({
  startAt: z.number(),
  endAt: z.number(),
  status: z.enum(["open", "locked", "exported"]).default("open"),
  lockedByUid: z.string().nullable().default(null),
  lockedAt: z.number().nullable().default(null),
  createdAt: z.number(),
});
export type PayPeriod = z.infer<typeof PayPeriodSchema>;

export const PayrollRunLineSchema = z.object({
  uid: z.string(),
  displayName: z.string(),
  regularMinutes: z.number().int().nonnegative(),
  overtimeMinutes: z.number().int().nonnegative().default(0),
  breakMinutes: z.number().int().nonnegative().default(0),
  hourlyRateCents: z.number().int().nonnegative().nullable().default(null),
  /** Hourly pay (regular + overtime); null when the person has no rate. */
  grossCents: z.number().int().nonnegative().nullable().default(null),
  /** Piece-rate pay for jobs finished in the period. */
  pieceCents: z.number().int().nonnegative().default(0),
  /** Commissions earned in the period. */
  commissionCents: z.number().int().nonnegative().default(0),
  /** Hourly + piece + commission. */
  totalCents: z.number().int().nonnegative().default(0),
});
export type PayrollRunLine = z.infer<typeof PayrollRunLineSchema>;

export const PayrollRunSchema = z.object({
  payPeriodId: z.string(),
  providerSlug: z.string().nullable().default(null), // e.g. "gusto", "payroll-csv"
  status: z.enum(["draft", "exported", "synced", "failed"]).default("draft"),
  lines: z.array(PayrollRunLineSchema).default([]),
  totalRegularMinutes: z.number().int().nonnegative().default(0),
  totalOvertimeMinutes: z.number().int().nonnegative().default(0),
  totalGrossCents: z.number().int().nonnegative().default(0),
  totalPieceCents: z.number().int().nonnegative().default(0),
  totalCommissionCents: z.number().int().nonnegative().default(0),
  exportPath: z.string().nullable().default(null), // Storage path of generated file
  error: z.string().nullable().default(null),
  createdByUid: z.string(),
  createdAt: z.number(),
});
export type PayrollRun = z.infer<typeof PayrollRunSchema>;
